/**
 * Safety-number / fingerprint implementation.
 *
 * Algorithm mirrors `kaether_for_ios/Stoat/E2EE/Fingerprint.swift` exactly so
 * that iOS and web users see the same 61-character safety number for the same
 * identity-key pair. iOS is treated as canonical (Signal-iOS is widely
 * audited); any future divergence should be resolved by updating this file to
 * match iOS, not the other way around.
 *
 * Algorithm summary (see kaether_for_web/docs/fingerprint.md for full spec):
 *
 *   1. Initial input = [0x00, 0x00] ‖ identityKey(32 bytes) ‖ userId(UTF-8)
 *   2. hash₀ = SHA-256(initial input)
 *   3. For i = 1..5199: hashᵢ = SHA-256(hashᵢ₋₁ ‖ identityKey)
 *   4. Take first 30 bytes of hash₅₁₉₉
 *   5. Split into 6 groups of 5 bytes; each group → big-endian uint40 % 100_000
 *      padded to 5 decimal digits (no separators within a half)
 *   6. Concatenate both halves with a single space, lowest-userId half first
 *
 * Output: "XXXXXXXXXXXXXXXXXXXXXXXXXXXXXX YYYYYYYYYYYYYYYYYYYYYYYYYYYYYY"
 *         (30 decimal digits, one space, 30 decimal digits = 61 chars total)
 *
 * PREVIOUS IMPLEMENTATION NOTE: The original fingerprint.ts delegated to
 * `@privacyresearch/libsignal-protocol-typescript`'s FingerprintGenerator which
 * uses SHA-512 and a different iteration structure — diverging from iOS. That
 * dependency has been replaced by this standalone SubtleCrypto implementation
 * to ensure bit-identical output with the iOS counterpart.
 */

import type { StorageType } from "@privacyresearch/libsignal-protocol-typescript";
import type { SignalProtocolStore } from "../store/SignalProtocolStore";

const ITERATIONS = 5200; // 1 initial + 5199 additional, matching iOS
const VERSION_BYTES = new Uint8Array([0x00, 0x00]);
const IDENTITY_KEY_LENGTH = 32;

/**
 * Compute the per-identity fingerprint half using the iOS-canonical algorithm.
 *
 * @param identityKey  Raw 32-byte Curve25519 public key (no leading type byte).
 * @param userId       Stable user identifier (e.g. a UUID or phone number string).
 * @returns            30-digit decimal string (6 × 5-digit segments, no spaces).
 */
export async function computeFingerprintHalf(
  identityKey: Uint8Array,
  userId: string,
): Promise<string> {
  if (identityKey.length !== IDENTITY_KEY_LENGTH) {
    throw new Error(
      `computeFingerprintHalf: identityKey must be exactly ${IDENTITY_KEY_LENGTH} bytes, got ${identityKey.length}`,
    );
  }

  const userIdBytes = new TextEncoder().encode(userId);

  // Step 1: build initial input = [0x00, 0x00] ‖ identityKey ‖ userId
  const initialInput = new Uint8Array(
    VERSION_BYTES.length + identityKey.length + userIdBytes.length,
  );
  initialInput.set(VERSION_BYTES, 0);
  initialInput.set(identityKey, VERSION_BYTES.length);
  initialInput.set(userIdBytes, VERSION_BYTES.length + identityKey.length);

  // Step 2: hash₀ = SHA-256(initialInput)
  let hashBuf = await crypto.subtle.digest("SHA-256", initialInput);

  // Step 3: for i = 1..5199: hashᵢ = SHA-256(hashᵢ₋₁ ‖ identityKey)
  for (let i = 1; i < ITERATIONS; i++) {
    const next = new Uint8Array(hashBuf.byteLength + identityKey.length);
    next.set(new Uint8Array(hashBuf), 0);
    next.set(identityKey, hashBuf.byteLength);
    hashBuf = await crypto.subtle.digest("SHA-256", next);
  }

  // Step 4: take first 30 bytes of final hash
  const bytes = new Uint8Array(hashBuf);

  // Step 5: encode as 6 groups of 5 decimal digits
  const segments: string[] = [];
  for (let i = 0; i < 30; i += 5) {
    // Interpret 5 bytes as a big-endian 40-bit unsigned integer
    let v = BigInt(0);
    for (let j = 0; j < 5; j++) {
      v = (v << BigInt(8)) | BigInt(bytes[i + j]);
    }
    segments.push(String(Number(v % BigInt(100_000))).padStart(5, "0"));
  }

  return segments.join(""); // 30 decimal digits, no separators
}

/**
 * Generate the combined safety number for a (local, remote) identity pair.
 *
 * The two halves are ordered by userId string comparison (< operator) so that
 * both endpoints independently compute the same 61-character string regardless
 * of which side calls the function.
 *
 * @param localIdentityKey   32-byte Curve25519 public key for the local user.
 * @param localUserId        Stable identifier for the local user.
 * @param remoteIdentityKey  32-byte Curve25519 public key for the remote user.
 * @param remoteUserId       Stable identifier for the remote user.
 * @returns  "XXXXXXXXXXXXXXXXXXXXXXXXXXXXXX YYYYYYYYYYYYYYYYYYYYYYYYYYYYYY" (61 chars)
 */
export async function generateFingerprintRaw(
  localIdentityKey: Uint8Array,
  localUserId: string,
  remoteIdentityKey: Uint8Array,
  remoteUserId: string,
): Promise<string> {
  const [localHalf, remoteHalf] = await Promise.all([
    computeFingerprintHalf(localIdentityKey, localUserId),
    computeFingerprintHalf(remoteIdentityKey, remoteUserId),
  ]);

  // Lowest userId goes first — order-independent result.
  if (localUserId < remoteUserId) {
    return `${localHalf} ${remoteHalf}`;
  } else {
    return `${remoteHalf} ${localHalf}`;
  }
}

/**
 * High-level helper that reads keys from the Signal store and delegates to
 * `generateFingerprintRaw`. Returns `null` if either identity is missing from
 * the store.
 */
export async function generateFingerprint(
  store: SignalProtocolStore,
  localUserId: string,
  remoteUserId: string,
  remoteDeviceId: number,
): Promise<string | null> {
  const localIdentity = await store.getIdentityKeyPair();
  if (!localIdentity) return null;

  const remoteIdentity = await store.getRemoteIdentity(
    `${remoteUserId}.${remoteDeviceId}`,
  );
  if (!remoteIdentity) return null;

  // libsignal keys may include a leading 0x05 type byte; strip it to obtain
  // the raw 32-byte key that the algorithm expects.
  const stripType = (buf: ArrayBuffer): Uint8Array => {
    const arr = new Uint8Array(buf);
    return arr.length === 33 && arr[0] === 0x05 ? arr.slice(1) : arr;
  };

  return generateFingerprintRaw(
    stripType(localIdentity.pubKey),
    localUserId,
    stripType(remoteIdentity),
    remoteUserId,
  );
}

export async function checkIdentityChanged(
  store: StorageType & {
    getRemoteIdentity: (addr: string) => Promise<ArrayBuffer | undefined>;
  },
  remoteUserId: string,
  remoteDeviceId: number,
  newIdentityKey: ArrayBuffer,
): Promise<boolean> {
  const address = `${remoteUserId}.${remoteDeviceId}`;
  const existing = await store.getRemoteIdentity(address);
  if (!existing) return false;

  const existingArr = new Uint8Array(existing);
  const newArr = new Uint8Array(newIdentityKey);

  if (existingArr.length !== newArr.length) return true;
  for (let i = 0; i < existingArr.length; i++) {
    if (existingArr[i] !== newArr[i]) return true;
  }
  return false;
}
